import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within a ThemeProvider');
  return context;
};

const STORAGE_KEY = 'bedtimeMode';

const isInBedtimeHours = () => {
  const h = new Date().getHours();
  return h >= 19 || h < 6;
};

export const ThemeProvider = ({ children }) => {
  // bedtimeMode setting van de gebruiker: 'off' | 'on' | 'auto'
  const [bedtimeMode, setBedtimeMode] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'on' || saved === 'off' || saved === 'auto') return saved;
    if (saved === 'true') return 'on';   // legacy boolean
    return 'auto';
  });

  // Voor auto-stand: hertest elke minuut of we in het bedtime-venster zitten
  const [autoActive, setAutoActive] = useState(isInBedtimeHours());
  useEffect(() => {
    if (bedtimeMode !== 'auto') return;
    setAutoActive(isInBedtimeHours());
    const id = setInterval(() => setAutoActive(isInBedtimeHours()), 60 * 1000);
    return () => clearInterval(id);
  }, [bedtimeMode]);

  // Effectief: aan of uit
  const isBedtimeMode = useMemo(() => {
    if (bedtimeMode === 'on') return true;
    if (bedtimeMode === 'off') return false;
    return autoActive;
  }, [bedtimeMode, autoActive]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, bedtimeMode);
  }, [bedtimeMode]);

  useEffect(() => {
    const root = document.documentElement;
    if (isBedtimeMode) root.classList.add('bedtime');
    else root.classList.remove('bedtime');
  }, [isBedtimeMode]);

  const setBedtimeModeSetting = useCallback((mode) => {
    if (['on', 'off', 'auto'].includes(mode)) setBedtimeMode(mode);
  }, []);

  const toggleBedtimeMode = useCallback(() => {
    setBedtimeMode((m) => (m === 'off' ? 'on' : 'off'));
  }, []);

  return (
    <ThemeContext.Provider value={{
      isBedtimeMode,
      bedtimeMode,
      setBedtimeMode: setBedtimeModeSetting,
      toggleBedtimeMode,
    }}>
      {children}
    </ThemeContext.Provider>
  );
};
